import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import backendApi from '../shared/api/backendApi';

const normalizeAccessRole = (role) => {
  const normalizedRole = String(role || '').trim().toLowerCase();
  if (normalizedRole === 'owner' || normalizedRole === 'admin') {
    return 'admin';
  }
  if (normalizedRole === 'staff' || normalizedRole === 'viewer') {
    return 'staff';
  }
  return 'admin';
};

function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isStaffUser, setIsStaffUser] = useState(false);

  const loadInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await backendApi.getInventory();
      // Database API returns { success: true, inventory: [...], totalItems: number, generatedAt: string }
      setInventory(response.inventory || []);
    } catch (err) {
      setError('Failed to load inventory. Please try again.');
      console.error('Error loading inventory:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const rawUserData = localStorage.getItem('userData');
    if (rawUserData) {
      try {
        const parsedUserData = JSON.parse(rawUserData);
        const accessRole = normalizeAccessRole(parsedUserData?.accessRole);
        setIsStaffUser(accessRole === 'staff' || Boolean(parsedUserData?.isStaff));
      } catch (parseError) {
        console.warn('Unable to parse userData for inventory role checks:', parseError);
      }
    }

    loadInventory();
  }, [loadInventory]);

  const filteredInventory = useMemo(
    () =>
      inventory.filter((item) => {
        if (!searchTerm.trim()) return true;

        const searchLower = searchTerm.toLowerCase();
        const itemName = (item.name || '').toLowerCase();
        const unit = (item.unit || '').toLowerCase();
        const sku = (item.sku || '').toLowerCase();

        return (
          itemName.includes(searchLower) ||
          unit.includes(searchLower) ||
          sku.includes(searchLower) ||
          (!isStaffUser && String(item.avg_purchase_price || '').includes(searchLower)) ||
          String(item.selling_price || '').includes(searchLower) ||
          String(item.current_stock || '').includes(searchLower)
        );
      }),
    [inventory, isStaffUser, searchTerm],
  );

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }),
    [],
  );

  const totalInventoryValue = useMemo(
    () =>
      filteredInventory.reduce((sum, item) => {
        const remainingQty = parseFloat(item.current_stock || 0);
        const sellingPrice = parseFloat(item.selling_price || 0);
        return sum + remainingQty * sellingPrice;
      }, 0),
    [filteredInventory],
  );

  const inventoryRows = useMemo(
    () =>
      filteredInventory
        .slice()
        .reverse()
        .map((item, index) => {
          const currentStock = parseFloat(item.current_stock || 0);
          const sellingPrice = parseFloat(item.selling_price || 0);
          const inventoryValue = currentStock * sellingPrice;
          const avgPurchasePrice = parseFloat(item.avg_purchase_price || 0);
          const qtyColor =
            currentStock <= (item.min_stock_level || 5) ? 'error.main' : 'success.main';

          return {
            key: `${item.id || item.sku || item.name || 'inv'}-${index}`,
            index,
            name: item.name,
            sku: item.sku || 'N/A',
            unit: item.unit,
            avgPurchasePriceFormatted: currencyFormatter.format(avgPurchasePrice),
            sellingPriceFormatted: currencyFormatter.format(sellingPrice),
            currentStockFormatted: currencyFormatter.format(currentStock),
            inventoryValueFormatted: currencyFormatter.format(inventoryValue),
            currentStockColor: qtyColor,
          };
        }),
    [currencyFormatter, filteredInventory],
  );

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header section with title and card side by side */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        {/* Left side - Title and description */}
        <Box sx={{ flex: 1, mr: 3 }}>
          <Typography variant="h4" gutterBottom>
            Inventory Status
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            This data is automatically updated from your purchase and sales transactions.
          </Typography>

          {/* Search Bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              placeholder={
                isStaffUser
                  ? 'Search inventory by item, unit, quantity, or selling price...'
                  : 'Search inventory by item, unit, quantity, or price...'
              }
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              size="small"
              sx={{ flex: 1, maxWidth: 500 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />
            {searchTerm && (
              <Typography variant="body2" color="text.secondary">
                Found {filteredInventory.length} of {inventory.length} items
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right side - Total Inventory Value Card */}
        <Box sx={{ flexShrink: 0, width: 280 }}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 3,
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Total Inventory Value
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                {'\u20B9'}{currencyFormatter.format(totalInventoryValue)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Based on current selling prices
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2, maxHeight: '75vh' }} stickyHeader>
        <Table>
          <TableHead sx={{ backgroundColor: '#1e293b', position: 'sticky', top: 0, zIndex: 1 }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Item</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>SKU</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Unit</TableCell>
              {!isStaffUser && (
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Avg Purchase Price</TableCell>
              )}
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Selling Price</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Current Stock</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Inventory Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inventoryRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isStaffUser ? 6 : 7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm
                      ? 'No inventory items found matching your search.'
                      : 'No inventory yet. Add purchases to build your inventory.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              inventoryRows.map((row) => (
                <TableRow
                  key={row.key}
                  sx={{
                    backgroundColor: row.index % 2 === 0 ? 'white' : '#f8fafc',
                    '&:hover': { backgroundColor: '#e2e8f0' },
                  }}
                >
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.sku}</TableCell>
                  <TableCell>{row.unit}</TableCell>
                  {!isStaffUser && <TableCell>{'\u20B9'}{row.avgPurchasePriceFormatted}</TableCell>}
                  <TableCell>{'\u20B9'}{row.sellingPriceFormatted}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 800, color: row.currentStockColor }}>
                      {row.currentStockFormatted}
                    </Typography>
                  </TableCell>
                  <TableCell>{'\u20B9'}{row.inventoryValueFormatted}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default Inventory;
